import { Component, OnDestroy, OnInit, Renderer2, Type } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CompanyApiService } from '@digilize/shared/data/services/src';
import { DialogService } from '@digilize/shared/utils/services/src';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';

import { SuccessPopupComponent } from '../../components/success-popup/success-popup.component';
import { CompanyColorsService } from '../../services/company-colors.service';
import { WizardService } from '../../services/wizard.service';
import { slider } from './animations';
import { CvSkillsComponent } from './cv-skills/cv-skills.component';
import { DesiredOccupationsComponent } from './desired-occupations/desired-occupations.component';
import { ProfessionSkillsComponent } from './profession-skills/profession-skills.component';
import { SelectYourJobComponent } from './select-your-job/select-your-job.component';
import { UploadYourCvComponent } from './upload-your-cv/upload-your-cv.component';
import { EducationLevelSelectionComponent } from './education-level-selection/education-level-selection.component';
import { LanguagesKnowledgeComponent } from './languages-knowledge/languages-knowledge.component';
import { UploadedCvComponent } from './uploaded-cv/uploaded-cv.component';

@Component({
  selector: 'bwc-have-resume',
  templateUrl: './have-resume.component.html',
  styleUrls: ['./have-resume.component.scss'],
  animations: [slider],
  providers: [WizardService],
})
export class HaveResumeComponent implements OnInit, OnDestroy {
  companyId: string;
  vacancyId: string;
  openFormLink: string;
  inviteLink: string;
  source: string;

  private subscriptions: Subscription[] = [];

  constructor(
    private companyColorsService: CompanyColorsService,
    private route: ActivatedRoute,
    private router: Router,
    private companyApiServices: CompanyApiService,
    private renderer: Renderer2,
    private dialogService: DialogService,
    public wizardService: WizardService
  ) {}

  ngOnInit(): void {
    const flow: Type<unknown>[] = [
      UploadYourCvComponent,
      UploadedCvComponent,
      SelectYourJobComponent,
      DesiredOccupationsComponent,
      CvSkillsComponent,
      ProfessionSkillsComponent,
      EducationLevelSelectionComponent,
      LanguagesKnowledgeComponent,
    ];

    this.wizardService.setFlow(flow);

    this.restoreStepFromUrl();
    this.checkParams();

    this.subscriptions.push(
      this.wizardService.currentStep$.pipe(skip(1)).subscribe((step) => {
        this.updateStepQueryParam(step);
      })
    );

    this.subscriptions.push(
      this.wizardService.completed$.subscribe(() => {
        this.onWizardComplete();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  private restoreStepFromUrl(): void {
    const stepParam = this.route.snapshot.queryParams['step'];
    const step = Number(stepParam);
    if (stepParam !== undefined && !isNaN(step) && step > 0 && step < this.wizardService.totalSteps) {
      this.wizardService.goToStep(step);
    }
  }

  private checkParams() {
    this.subscriptions.push(
      this.route.queryParams.subscribe((params) => {
        const vacancyId = params['apply_to'];
        const openFormLink = params['ofl'];
        const inviteLink = params['invite'];

        if (vacancyId) {
          this.vacancyId = vacancyId;
        }
        if (openFormLink) {
          this.openFormLink = openFormLink;
        }
        if (inviteLink) {
          this.inviteLink = inviteLink;
        }

        const source = params['source'];
        if (source) {
          this.source = source;
        }
      })
    );
  }

  private getCompanyStyles() {
    this.companyApiServices.getCompanyStyles(this.companyId).subscribe((data: any) => {
      this.companyColorsService.setCompanyColors(data.colors_model);
      this.renderer.addClass(document.body, 'company-colors');
    });
  }

  private getCompanyStylesByDomain() {
    this.companyApiServices.getCompanyStylesByDomain().subscribe((data: any) => {
      this.companyColorsService.setCompanyColors(data.colors_model);
      this.renderer.addClass(document.body, 'company-colors');
    });
  }

  private updateStepQueryParam(step: number) {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { step },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private onWizardComplete(): void {
    const dialog = this.dialogService.openDialog(SuccessPopupComponent, {
      disableClose: false,
      backdropClass: 'success-backdrop',
      panelClass: 'success-dialog-container',
      width: '948px',
    });
    dialog.afterClosed().subscribe(() => {
      this.renderer.removeClass(document.body, 'company-colors');
      if (this.vacancyId) {
        this.router.navigate(['/profile-check'], {
          queryParams: { apply_to: this.vacancyId, c: this.companyId, ofl: this.openFormLink },
        });
      } else if (this.openFormLink) {
        this.router.navigate(['/profile-check'], {
          queryParams: { ofl: this.openFormLink },
        });
      } else {
        window.location.href = '/profile-check';
      }
    });
  }
}
